/**
 * Team UI service for client-side operations
 * Provides API calls for team form interface CRUD and AI schema generation
 */

import { api } from "../utils/api-client";
import type {
  TeamUiRecord,
  FormSchema,
  DescriptionGenerationResponse,
  AgentGenerationResponse,
  RefinementResponse,
} from "../types/team-ui-types";

/** Wrapped API response shape from server */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Fetches all UI interfaces for a team
 */
export async function getTeamUis(
  teamId: string
): Promise<TeamUiRecord[]> {
  const response: ApiResponse<TeamUiRecord[]> = await api.get(
    `/teams/${teamId}/uis`
  );
  return response.data;
}

/**
 * Fetches a single UI interface by ID
 */
export async function getTeamUiById(
  teamId: string,
  uiId: string
): Promise<TeamUiRecord> {
  const response: ApiResponse<TeamUiRecord> = await api.get(
    `/teams/${teamId}/uis/${uiId}`
  );
  return response.data;
}

/**
 * Creates a new UI interface for a team
 */
export async function createTeamUi(
  teamId: string,
  data: {
    name: string;
    description?: string;
    formSchema: FormSchema;
    naturalLanguagePrompt?: string;
  }
): Promise<TeamUiRecord> {
  const response: ApiResponse<TeamUiRecord> = await api.post(
    `/teams/${teamId}/uis`,
    data
  );
  return response.data;
}

/**
 * Updates an existing UI interface
 */
export async function updateTeamUi(
  teamId: string,
  uiId: string,
  data: {
    name?: string;
    description?: string | null;
    formSchema?: FormSchema;
    naturalLanguagePrompt?: string | null;
  }
): Promise<TeamUiRecord> {
  const response: ApiResponse<TeamUiRecord> = await api.put(
    `/teams/${teamId}/uis/${uiId}`,
    data
  );
  return response.data;
}

/**
 * Deletes a UI interface
 */
export async function deleteTeamUi(
  teamId: string,
  uiId: string
): Promise<void> {
  await api.delete(`/teams/${teamId}/uis/${uiId}`);
}

/**
 * Toggles the published status of a UI interface
 */
export async function togglePublish(
  teamId: string,
  uiId: string
): Promise<TeamUiRecord> {
  const response: ApiResponse<TeamUiRecord> = await api.post(
    `/teams/${teamId}/uis/${uiId}/publish`
  );
  return response.data;
}

/**
 * Sets a UI as the default interface for the team
 */
export async function setDefaultUi(
  teamId: string,
  uiId: string
): Promise<TeamUiRecord> {
  const response: ApiResponse<TeamUiRecord> = await api.post(
    `/teams/${teamId}/uis/${uiId}/default`
  );
  return response.data;
}

/**
 * Generates a form schema from a natural language description using AI
 */
export async function generateSchemaFromDescription(
  teamId: string,
  description: string,
  agentContext?: { name?: string; description?: string }
): Promise<DescriptionGenerationResponse> {
  const response: ApiResponse<DescriptionGenerationResponse> = await api.post(
    `/teams/${teamId}/uis/generate-schema/description`,
    { description, agentContext }
  );
  return response.data;
}

/**
 * Refines an existing form schema with a natural language instruction
 */
export async function refineSchema(
  teamId: string,
  currentSchema: FormSchema,
  instruction: string
): Promise<RefinementResponse> {
  const response: ApiResponse<RefinementResponse> = await api.post(
    `/teams/${teamId}/uis/generate-schema/refine`,
    { currentSchema, instruction }
  );
  return response.data;
}

/**
 * Auto-generates a form schema by analyzing the team's configuration and agents
 */
export async function generateSchemaFromTeam(
  teamId: string
): Promise<AgentGenerationResponse> {
  const response: ApiResponse<AgentGenerationResponse> = await api.post(
    `/teams/${teamId}/uis/generate-schema/team`
  );
  return response.data;
}

/**
 * Fetches the default UI for a team (public endpoint, no auth required)
 */
export async function getDefaultTeamUi(
  teamId: string
): Promise<TeamUiRecord & { teamInfo?: { name: string; description: string | null; icon: string | null } }> {
  const response = await api.get(
    `/public/teams/${teamId}/uis/default`,
    { requiresAuth: false, showErrorToast: false }
  );
  return response.data;
}
